import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";

const LEVELS = ["", "EMR", "EMT", "AEMT", "Paramedic", "CriticalCare"];
const DIFFICULTIES = ["", "easy", "medium", "hard"];
const QUESTION_TYPES = ["", "multichoice", "multiselect", "fill_in_blank", "free_response", "numeric_equation", "custom"];
const STATUSES = ["", "draft", "approved"];
const CATEGORIES = ["all", "institution", "public"];

const LOCAL_STORAGE_CONVERSATION_KEY = "question_bank_conversation_id";

function toDisplayText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message || fallback;
  if (typeof value === "object") {
    if (typeof value.message === "string") return value.message;
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return String(value);
}

function hasSystemAdminRole(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  return roles.some((role) => {
    if (typeof role === "string") return role === "system-admin";
    return role?.key === "system-admin" || role?.name === "system-admin";
  });
}

function QuestionCard({ question, onApprove, onApprovePublic, onDraft, onUpdateDraft, canPublishPublic = true }) {
  const [editStem, setEditStem] = useState(question.stem || "");
  const [editing, setEditing] = useState(false);

  return (
    <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={3} bg="gray.900">
      <HStack spacing={2} mb={2} flexWrap="wrap">
        <Badge colorScheme={question.status === "approved" ? "green" : "orange"}>{question.status || "draft"}</Badge>
        {question.is_public && <Badge colorScheme="purple">public</Badge>}
        {question.institution && <Badge colorScheme="cyan">{question.institution}</Badge>}
        <Badge>{question.level || "Paramedic"}</Badge>
        <Badge>{question.question_type || "multichoice"}</Badge>
        <Badge>{question.difficulty || "medium"}</Badge>
        <Badge>{question.topic || question.domain || "General EMS"}</Badge>
      </HStack>

      {editing ? (
        <Textarea value={editStem} onChange={(e) => setEditStem(e.target.value)} size="sm" minH="80px" />
      ) : (
        <Text fontSize="sm" color="gray.100" whiteSpace="pre-wrap">{question.stem}</Text>
      )}

      {Array.isArray(question.options) && question.options.length > 0 && (
        <VStack align="stretch" mt={2} spacing={1}>
          {question.options.map((opt, idx) => (
            <Text key={opt.id || idx} fontSize="xs" color="gray.300">{opt.id || String.fromCharCode(65 + idx)}. {opt.text}</Text>
          ))}
        </VStack>
      )}

      <HStack mt={3} spacing={2} flexWrap="wrap">
        {!editing && (
          <Button size="xs" onClick={() => setEditing(true)}>Edit Stem</Button>
        )}
        {editing && (
          <>
            <Button
              size="xs"
              colorScheme="blue"
              onClick={() => {
                onUpdateDraft({ ...question, stem: editStem });
                setEditing(false);
              }}
            >
              Save Edit
            </Button>
            <Button size="xs" variant="outline" onClick={() => { setEditStem(question.stem || ""); setEditing(false); }}>
              Cancel
            </Button>
          </>
        )}
        <Button size="xs" colorScheme="green" onClick={() => onApprove(question)}>Approve</Button>
        {canPublishPublic && (
          <Button size="xs" colorScheme="purple" onClick={() => onApprovePublic(question)}>Approve + Public</Button>
        )}
        <Button size="xs" colorScheme="orange" onClick={() => onDraft(question)}>Draft</Button>
      </HStack>
    </Box>
  );
}

export default function QuestionBankRoute() {
  const user = useUser();
  const isSystemAdmin = hasSystemAdminRole(user);

  const [level, setLevel] = useState("");
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [desiredCount, setDesiredCount] = useState(5);

  const [selectedInstitution, setSelectedInstitution] = useState("");
  const [institutions, setInstitutions] = useState([]);

  const [conversationId, setConversationId] = useState(() => localStorage.getItem(LOCAL_STORAGE_CONVERSATION_KEY) || "");
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [suggestedItems, setSuggestedItems] = useState([]);
  const [infoMessage, setInfoMessage] = useState("");
  const [bankTabIndex, setBankTabIndex] = useState(0);

  const [{ data: bankData, fetching: loadingBank }, getQuestionBank] = useGlobalAction(api.getQuestionBank);
  const [{ data: institutionsData }, getQuestionBankInstitutions] = useGlobalAction(api.getQuestionBankInstitutions);
  const [{ fetching: chatting }, questionBankChat] = useGlobalAction(api.questionBankChat);
  const [{ fetching: savingQuestion }, upsertQuestionBankItem] = useGlobalAction(api.upsertQuestionBankItem);

  useEffect(() => {
    const run = async () => {
      if (!user?.id) return;
      const result = await getQuestionBankInstitutions({});
      if (result?.success) {
        const list = Array.isArray(result.institutions) ? result.institutions : [];
        setInstitutions(list);
        const fallbackInstitution = result.currentInstitution || user?.institution || list[0] || "";
        setSelectedInstitution((prev) => prev || fallbackInstitution);
      }
    };
    run();
  }, [user?.id]);

  const activeInstitution = selectedInstitution || user?.institution || "";

  const loadBank = async () => {
    if (!activeInstitution) return;
    await getQuestionBank({
      institution: activeInstitution,
      category,
      includePublic: category !== "institution",
      level: level || undefined,
      topic: topic || undefined,
      difficulty: difficulty || undefined,
      questionType: questionType || undefined,
      status: status || undefined,
      search: search || undefined,
      limit: 250,
      publicLimit: 250,
    });
  };

  useEffect(() => {
    loadBank();
  }, [activeInstitution, category]);

  const questions = bankData?.questions || [];

  const approved = useMemo(() => questions.filter((q) => q.status === "approved"), [questions]);
  const draft = useMemo(() => questions.filter((q) => q.status !== "approved"), [questions]);
  const publicQuestions = useMemo(() => questions.filter((q) => q.is_public === true), [questions]);

  const onSendChat = async () => {
    if (!chatInput.trim() || !activeInstitution) return;

    const message = chatInput.trim();
    const requestId = `qb-${Date.now().toString().slice(-6)}`;
    const requestedCount = Number(desiredCount) || 5;
    setInfoMessage("");
    setChatInput("");
    setChatMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: message,
        timestamp: new Date().toISOString(),
        requestId,
        requestedCount,
      },
    ]);

    try {
      const result = await questionBankChat({
        conversationId: conversationId || undefined,
        institution: activeInstitution,
        message,
        level: level || undefined,
        topic: topic || undefined,
        difficulty: difficulty || undefined,
        questionType: questionType || undefined,
        desiredCount: requestedCount,
        autoSaveDraft: true,
      });

      if (!result?.success) {
        setInfoMessage(toDisplayText(result?.message || result?.error, "Chat failed"));
        return;
      }

      if (result.conversationId && result.conversationId !== conversationId) {
        setConversationId(result.conversationId);
        localStorage.setItem(LOCAL_STORAGE_CONVERSATION_KEY, result.conversationId);
      }

      if (result.status && result.status !== "completed") {
        setInfoMessage(`[${requestId}] ${toDisplayText(result?.message, `Chat status: ${result.status}.`)}`);
        return;
      }

      if (result.assistantMessage) {
        setChatMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: toDisplayText(result.assistantMessage, ""),
            timestamp: new Date().toISOString(),
            requestId,
            requestedCount,
          },
        ]);
      }

      const items = result.suggestions?.items || [];
      setSuggestedItems(items);
      if (items.length > 0) {
        setBankTabIndex(1); // Draft tab
      }
      const countSummary = `${items.length}/${requestedCount} generated`;
      setInfoMessage(
        result.draftRecordId
          ? `[${requestId}] ${countSummary}. Saved draft record ${result.draftRecordId}.`
          : `[${requestId}] ${countSummary}. Suggestions generated.`
      );
      await loadBank();
    } catch (error) {
      setInfoMessage(`[${requestId}] Chat failed: ${toDisplayText(error, "Unknown error")}`);
    }
  };

  const onChatKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (e.shiftKey) return; // allow multiline input
    if (!e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      onSendChat();
      return;
    }
    e.preventDefault();
    onSendChat();
  };

  const upsertStatus = async (question, nextStatus, isPublic = false) => {
    const result = await upsertQuestionBankItem({
      institution: activeInstitution,
      status: nextStatus,
      isPublic,
      question,
      name: `${question.topic || question.domain || "Question"} - ${new Date().toISOString()}`,
    });

    if (!result?.success) {
      setInfoMessage(result?.message || "Failed to save question");
      return;
    }

    setInfoMessage(`Question ${nextStatus}${isPublic ? " (public)" : ""}`);
    await loadBank();
  };

  const onApprove = async (question) => upsertStatus(question, "approved", false);
  const onApprovePublic = async (question) => upsertStatus(question, "approved", true);
  const onDraft = async (question) => upsertStatus(question, "draft", false);
  const onUpdateDraft = async (question) => upsertStatus(question, "draft", false);

  return (
    <Box p={4} color="white">
      <VStack align="stretch" spacing={4}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">Instructor Question Bank</Text>
          <Text color="gray.400" fontSize="sm">Durable institution chat + draft/approved/public bank with level/topic/difficulty/type filters.</Text>
        </Box>

        {infoMessage && (
          <Alert status="info" bg="blue.900" borderRadius="md">
            <AlertIcon />
            {infoMessage}
          </Alert>
        )}

        <Grid templateColumns={{ base: "1fr", lg: "1.1fr 1fr" }} gap={4}>
          <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={3} bg="gray.800">
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Question Authoring Chat</Text>

              {isSystemAdmin && (
                <Select value={activeInstitution} onChange={(e) => setSelectedInstitution(e.target.value)} size="sm">
                  {institutions.map((inst) => (
                    <option key={inst} value={inst}>{inst}</option>
                  ))}
                </Select>
              )}

              <HStack>
                <Select value={level} onChange={(e) => setLevel(e.target.value)} size="sm">
                  {LEVELS.map((v) => <option key={v} value={v}>{v || "All Levels"}</option>)}
                </Select>
                <Input size="sm" placeholder="Topic" value={topic} onChange={(e) => setTopic(e.target.value)} />
              </HStack>
              <HStack>
                <Select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} size="sm">
                  {DIFFICULTIES.map((v) => <option key={v} value={v}>{v || "All Difficulty"}</option>)}
                </Select>
                <Select value={questionType} onChange={(e) => setQuestionType(e.target.value)} size="sm">
                  {QUESTION_TYPES.map((v) => <option key={v} value={v}>{v || "All Question Types"}</option>)}
                </Select>
              </HStack>
              <HStack>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  size="sm"
                  value={desiredCount}
                  onChange={(e) => setDesiredCount(Number(e.target.value || 5))}
                  placeholder="Question Count"
                />
                <Button size="sm" onClick={loadBank} isLoading={loadingBank}>Refresh Bank</Button>
              </HStack>

              <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={2} minH="280px" maxH="420px" overflowY="auto" bg="gray.900">
                {chatMessages.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">Start by asking: "Generate 5 CriticalCare multiselect questions on ventilator management."</Text>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    {chatMessages.map((m, idx) => (
                      <Box key={`${m.timestamp}-${idx}`} p={2} borderRadius="md" bg={m.role === "user" ? "blue.800" : "gray.700"}>
                        <Text fontSize="xs" color="gray.200" mb={1}>{m.role === "user" ? "You" : "Assistant"}</Text>
                        {(m.requestId || m.requestedCount) && (
                          <Text fontSize="xs" color="gray.300" mb={1}>
                            {m.requestId ? `[${m.requestId}] ` : ""}{m.requestedCount ? `requested ${m.requestedCount}` : ""}
                          </Text>
                        )}
                        <Text fontSize="sm" whiteSpace="pre-wrap">{toDisplayText(m.content, "")}</Text>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>

              <HStack align="flex-end">
                <Textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={onChatKeyDown}
                  placeholder="Ask to generate, revise, or compare against existing questions..."
                  size="sm"
                  minH="80px"
                />
                <Button colorScheme="blue" onClick={onSendChat} isLoading={chatting}>Send</Button>
              </HStack>
              <Text fontSize="xs" color="gray.500">Press Enter to send, Shift+Enter for newline, Ctrl+Enter also sends.</Text>

              {suggestedItems.length > 0 && (
                <>
                  <Divider />
                  <Text fontSize="sm" color="gray.300">Suggested Questions ({suggestedItems.length})</Text>
                  <VStack align="stretch" spacing={2} maxH="320px" overflowY="auto">
                    {suggestedItems.map((q, idx) => (
                      <QuestionCard
                        key={q.item_id || idx}
                        question={{ ...q, status: "draft" }}
                        onApprove={onApprove}
                        onApprovePublic={onApprovePublic}
                        onDraft={onDraft}
                        onUpdateDraft={onUpdateDraft}
                        canPublishPublic={true}
                      />
                    ))}
                  </VStack>
                </>
              )}
            </VStack>
          </Box>

          <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={3} bg="gray.800">
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Durable Question Bank</Text>
              <HStack>
                <Select value={category} onChange={(e) => setCategory(e.target.value)} size="sm">
                  {CATEGORIES.map((v) => <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>)}
                </Select>
                <Select value={status} onChange={(e) => setStatus(e.target.value)} size="sm">
                  {STATUSES.map((v) => <option key={v} value={v}>{v || "All Status"}</option>)}
                </Select>
                <Input size="sm" placeholder="Search stem/topic" value={search} onChange={(e) => setSearch(e.target.value)} />
                <Button size="sm" onClick={loadBank}>Apply</Button>
              </HStack>

              {loadingBank ? (
                <Flex justify="center" py={8}><Spinner /></Flex>
              ) : (
                <Tabs
                  variant="enclosed"
                  colorScheme="blue"
                  index={bankTabIndex}
                  onChange={(index) => setBankTabIndex(index)}
                >
                  <TabList>
                    <Tab>Approved ({approved.length})</Tab>
                    <Tab>Draft ({draft.length})</Tab>
                    <Tab>Public ({publicQuestions.length})</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel p={2}>
                      <Stack spacing={2} maxH="640px" overflowY="auto">
                        {approved.length === 0 ? (
                          <Text color="gray.500" fontSize="sm">No approved questions found.</Text>
                        ) : approved.map((q) => (
                          <QuestionCard
                            key={`${q.source_record_id}-${q.question_id}`}
                            question={q}
                            onApprove={onApprove}
                            onApprovePublic={onApprovePublic}
                            onDraft={onDraft}
                            onUpdateDraft={onUpdateDraft}
                            canPublishPublic={q.institution === activeInstitution}
                          />
                        ))}
                      </Stack>
                    </TabPanel>
                    <TabPanel p={2}>
                      <Stack spacing={2} maxH="640px" overflowY="auto">
                        {draft.length === 0 ? (
                          <Text color="gray.500" fontSize="sm">No draft questions found.</Text>
                        ) : draft.map((q) => (
                          <QuestionCard
                            key={`${q.source_record_id}-${q.question_id}`}
                            question={q}
                            onApprove={onApprove}
                            onApprovePublic={onApprovePublic}
                            onDraft={onDraft}
                            onUpdateDraft={onUpdateDraft}
                            canPublishPublic={q.institution === activeInstitution}
                          />
                        ))}
                      </Stack>
                    </TabPanel>
                    <TabPanel p={2}>
                      <Stack spacing={2} maxH="640px" overflowY="auto">
                        {publicQuestions.length === 0 ? (
                          <Text color="gray.500" fontSize="sm">No public questions found.</Text>
                        ) : publicQuestions.map((q) => (
                          <QuestionCard
                            key={`${q.source_record_id}-${q.question_id}`}
                            question={q}
                            onApprove={onApprove}
                            onApprovePublic={onApprovePublic}
                            onDraft={onDraft}
                            onUpdateDraft={onUpdateDraft}
                            canPublishPublic={q.institution === activeInstitution}
                          />
                        ))}
                      </Stack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              )}

              {(chatting || savingQuestion) && (
                <Text fontSize="xs" color="gray.400">Working...</Text>
              )}
            </VStack>
          </Box>
        </Grid>
      </VStack>
    </Box>
  );
}
